// components/Sections/Admin/Matriculas/Estadisticas.jsx
//
// Estadísticas de matrículas de un recinto, calculadas desde la lista real de
// matrículas (`GET /matriculas/usuarios/:recintoId`), la misma que usa Usuarios
// matriculados, para que las dos pantallas cuadren.
//
// Antes se usaba `GET /matriculas/estadisticas/:id` y la pantalla completaba lo que
// faltaba inventándolo: "Tipo de vigencia" suponía que todas eran indefinidas, los
// pagos pendientes y rechazados eran 0 fijos y los ingresos eran compras × precio
// ACTUAL. Ese endpoint además filtra órdenes por estados que no existen
// ('completado', 'pendiente', 'rechazado'), así que sus pagos e ingresos dan 0.
import React, { useState, useEffect, useMemo } from 'react';
import { Select, Alert, Spin, Tooltip as AntTooltip } from 'antd';
import { FiRefreshCw, FiUsers, FiCheckCircle, FiClock, FiXCircle, FiDollarSign, FiInfo } from 'react-icons/fi';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import MatriculaService from '../../../../service/Matriculas';
import RecintoService from '../../../../service/Recintos';
import { formatearNombre } from '../../../../utils/texto';
import { EncabezadoAdmin, ChipEstado, ChipNeutro } from '../../../Ui/Admin/PiezasAdmin';
import { DIAS_POR_VENCER, estadoDe, tipoRegistro } from './estadoMatricula';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);
dayjs.locale('es');

const MESES = 12;
const clp = (n) => `$${Math.round(n || 0).toLocaleString('es-CL')}`;
const pct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0);

const Tarjeta = ({ children, className = '' }) => (
  <section className={`rounded-3xl bg-white border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,.04),0_8px_24px_-14px_rgba(0,0,0,.10)] ${className}`}>
    {children}
  </section>
);

const Reparto = ({ titulo, partes }) => {
  const total = partes.reduce((a, p) => a + p.n, 0);
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">{titulo}</p>
      <div className="flex h-2.5 rounded-full overflow-hidden bg-gray-100" aria-hidden>
        {partes.map(p => <span key={p.label} className={p.color} style={{ width: `${pct(p.n, total)}%` }} />)}
      </div>
      <ul className="m-0 mt-2.5 p-0 list-none space-y-1.5">
        {partes.map(p => (
          <li key={p.label} className="flex items-center justify-between gap-3 text-sm">
            <span className="flex items-center gap-2 text-gray-700"><span className={`w-2.5 h-2.5 rounded-full ${p.color}`} />{p.label}</span>
            <span className="tabular-nums"><strong className="text-gray-900">{p.n.toLocaleString('es-CL')}</strong> <span className="text-gray-400">· {pct(p.n, total)}%</span></span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const EstadisticasMatriculas = () => {
  const [recintos, setRecintos] = useState([]);
  const [recintoId, setRecintoId] = useState(null);
  const [matriculas, setMatriculas] = useState([]);
  const [config, setConfig] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [actualizado, setActualizado] = useState(null);
  const [recarga, setRecarga] = useState(0);

  useEffect(() => {
    RecintoService.List({ limit: 1000 })
      .then(res => {
        const lista = (res.data || []).slice().sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
        setRecintos(lista);
        if (lista.length) setRecintoId(lista[0]._id); else setCargando(false);
      })
      .catch(() => { setError('No se pudieron cargar los recintos'); setCargando(false); });
  }, []);

  // Una carga por recinto; `vigente` descarta una respuesta que llegue tarde.
  useEffect(() => {
    if (!recintoId) return undefined;
    let vigente = true;
    setCargando(true);
    setError(null);
    Promise.all([
      MatriculaService.ListarUsuariosMatriculados(recintoId),
      MatriculaService.ListarMatriculas({}).catch(() => ({ data: [] })),
    ])
      .then(([lista, configs]) => {
        if (!vigente) return;
        setMatriculas(Array.isArray(lista) ? lista : []);
        setConfig((configs?.data || []).find(c => c.recinto?._id === recintoId) || null);
        setActualizado(new Date());
      })
      .catch(e => { if (vigente) { setError(e?.error || e?.message || 'No se pudieron cargar las estadísticas'); setMatriculas([]); } })
      .finally(() => { if (vigente) setCargando(false); });
    return () => { vigente = false; };
  }, [recintoId, recarga]);

  const d = useMemo(() => {
    const estados = matriculas.map(estadoDe);
    const pagadas = matriculas.filter(m => tipoRegistro(m) === 'pago');
    const inicioMes = dayjs().startOf('month');
    const meses = Array.from({ length: MESES }, (_, i) => inicioMes.subtract(MESES - 1 - i, 'month'));
    const porMes = meses.map(mes => {
      const delMes = matriculas.filter(m => m.fechaInicio && dayjs(m.fechaInicio).isSame(mes, 'month'));
      return {
        mes,
        pago: delMes.filter(m => tipoRegistro(m) === 'pago').length,
        manual: delMes.filter(m => tipoRegistro(m) === 'manual').length,
      };
    });
    return {
      total: matriculas.length,
      activas: estados.filter(e => e === 'vigente' || e === 'por_vencer').length,
      porVencer: estados.filter(e => e === 'por_vencer').length,
      vencidas: estados.filter(e => e === 'vencida' || e === 'caducada').length,
      canceladas: estados.filter(e => e === 'cancelada' || e === 'revocacion_sin_aplicar').length,
      renovadas: estados.filter(e => e === 'renovada').length,
      pago: pagadas.length,
      manual: matriculas.length - pagadas.length,
      anual: matriculas.filter(m => m.tipoVigencia === 'anual').length,
      indefinida: matriculas.filter(m => m.tipoVigencia !== 'anual').length,
      recaudado: pagadas.reduce((a, m) => a + (m.valorPagado || 0), 0),
      recaudadoAnio: pagadas.filter(m => m.fechaInicio && dayjs(m.fechaInicio).isSame(dayjs(), 'year'))
        .reduce((a, m) => a + (m.valorPagado || 0), 0),
      porMes,
      nuevasMes: porMes[porMes.length - 1].pago + porMes[porMes.length - 1].manual,
    };
  }, [matriculas]);

  const nombreRecinto = recintos.find(r => r._id === recintoId)?.nombre || '';

  return (
    <div className="max-w-[1280px]">
      <EncabezadoAdmin
        titulo="Estadísticas de"
        destacado="matrículas"
        subtitulo={actualizado ? `Calculadas desde las matrículas registradas · actualizado ${actualizado.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}` : 'Calculadas desde las matrículas registradas'}
      >
        <Select value={recintoId} onChange={setRecintoId} style={{ minWidth: 240 }} showSearch optionFilterProp="label"
          aria-label="Recinto" options={recintos.map(r => ({ value: r._id, label: formatearNombre(r.nombre) }))} />
        <button type="button" onClick={() => setRecarga(n => n + 1)} disabled={cargando} aria-label="Actualizar"
          className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:border-primary hover:text-primary transition-colors disabled:opacity-60">
          <FiRefreshCw size={16} className={cargando ? 'animate-spin' : ''} />
        </button>
      </EncabezadoAdmin>

      {error && <Alert type="error" showIcon className="mb-4" message={error} />}

      {cargando && !matriculas.length ? (
        <div className="py-24 text-center"><Spin size="large" /></div>
      ) : !error && d.total === 0 ? (
        <Tarjeta className="py-16 text-center">
          <p className="font-bold text-gray-900 m-0">{nombreRecinto ? `${formatearNombre(nombreRecinto)} aún no tiene matrículas` : 'Selecciona un recinto'}</p>
          <p className="text-sm text-gray-500 m-0 mt-1">Cuando alguien compre o se registre una matrícula, sus números aparecerán aquí.</p>
        </Tarjeta>
      ) : !error && (
        <Spin spinning={cargando}>
          {/* ── Lo principal ── */}
          <section className="relative overflow-hidden rounded-3xl bg-[#00266b] text-white p-6 sm:p-7 mb-5">
            <div aria-hidden className="absolute inset-0 opacity-[.08] pointer-events-none" style={{
              backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '22px 22px',
            }} />
            <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-0 md:divide-x md:divide-white/10">
              <div className="md:pr-8">
                <p className="text-xs font-bold uppercase tracking-widest text-secondary m-0">Matrículas activas</p>
                <p className="text-5xl font-extrabold leading-tight m-0 mt-1 tabular-nums">{d.activas.toLocaleString('es-CL')}</p>
                <p className="text-sm text-blue-100 m-0 mt-1">{pct(d.activas, d.total)}% de {d.total.toLocaleString('es-CL')} registradas</p>
              </div>
              <div className="md:px-8">
                <p className="text-xs font-bold uppercase tracking-widest text-blue-200 m-0 flex items-center gap-1.5">
                  Recaudado
                  <AntTooltip title="Suma del valor pagado en cada matrícula comprada online. Las manuales no tienen pago asociado.">
                    <FiInfo size={12} className="cursor-help" />
                  </AntTooltip>
                </p>
                <p className="text-3xl font-extrabold m-0 mt-1 tabular-nums">{clp(d.recaudado)}</p>
                <p className="text-sm text-blue-100 m-0 mt-1">{clp(d.recaudadoAnio)} en {dayjs().year()}</p>
              </div>
              <div className="md:pl-8">
                <p className="text-xs font-bold uppercase tracking-widest text-blue-200 m-0">Nuevas este mes</p>
                <p className="text-3xl font-extrabold m-0 mt-1 tabular-nums">{d.nuevasMes.toLocaleString('es-CL')}</p>
                <p className="text-sm text-blue-100 m-0 mt-1 capitalize">{dayjs().format('MMMM YYYY')}</p>
              </div>
            </div>
          </section>

          {/* ── Estado ── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            {[
              { icon: <FiUsers size={19} />, label: 'Registradas', n: d.total, tono: 'bg-blue-50 text-primary' },
              { icon: <FiCheckCircle size={19} />, label: 'Activas', n: d.activas, tono: 'bg-blue-50 text-primary' },
              { icon: <FiClock size={19} />, label: `Vencen en ${DIAS_POR_VENCER} días`, n: d.porVencer, tono: d.porVencer ? 'bg-secondary/40 text-[#5c4300]' : 'bg-blue-50 text-primary' },
              { icon: <FiXCircle size={19} />, label: 'Vencidas', n: d.vencidas, tono: 'bg-gray-100 text-gray-500' },
            ].map(k => (
              <div key={k.label} className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-gray-100">
                <span className={`w-11 h-11 flex-shrink-0 rounded-xl flex items-center justify-center ${k.tono}`}>{k.icon}</span>
                <span>
                  <span className="block text-2xl font-extrabold text-gray-900 leading-none tabular-nums">{k.n.toLocaleString('es-CL')}</span>
                  <span className="block text-xs font-semibold text-gray-500 mt-1">{k.label}</span>
                </span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[1.5fr_1fr] gap-5">
            {/* ── Tendencia ── */}
            <Tarjeta className="p-6">
              <div className="flex items-end justify-between gap-3 mb-4">
                <h2 className="text-base font-extrabold text-gray-900 m-0">Matrículas nuevas <span className="hl-amarillo">por mes</span></h2>
                <span className="text-xs text-gray-500">Últimos {MESES} meses</span>
              </div>
              <div className="h-[260px]">
                <Bar
                  data={{
                    labels: d.porMes.map(m => m.mes.format('MMM').replace('.', '')),
                    datasets: [
                      { label: 'Pago online', data: d.porMes.map(m => m.pago), backgroundColor: '#0047bb', borderRadius: 4, stack: 's', maxBarThickness: 28 },
                      { label: 'Manual', data: d.porMes.map(m => m.manual), backgroundColor: '#feda2c', borderRadius: 4, stack: 's', maxBarThickness: 28 },
                    ],
                  }}
                  options={{
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'bottom', labels: { boxWidth: 10, boxHeight: 10, usePointStyle: true, color: '#374151' } },
                      tooltip: { callbacks: { title: (items) => d.porMes[items[0].dataIndex].mes.format('MMMM YYYY') } },
                    },
                    scales: {
                      x: { stacked: true, grid: { display: false }, ticks: { color: '#6b7280' } },
                      y: { stacked: true, beginAtZero: true, border: { display: false }, grid: { color: '#f1f5f9' }, ticks: { color: '#6b7280', precision: 0 } },
                    },
                  }}
                />
              </div>
            </Tarjeta>

            {/* ── Repartos + configuración ── */}
            <div className="space-y-5">
              <Tarjeta className="p-6 space-y-6">
                <Reparto titulo="Cómo se registraron" partes={[
                  { label: 'Pago online', n: d.pago, color: 'bg-primary' },
                  { label: 'Registro manual', n: d.manual, color: 'bg-secondary' },
                ]} />
                <Reparto titulo="Vigencia" partes={[
                  { label: 'Anual', n: d.anual, color: 'bg-primary' },
                  { label: 'Indefinida', n: d.indefinida, color: 'bg-blue-200' },
                ]} />
                {(d.canceladas > 0 || d.renovadas > 0) && (
                  <p className="text-xs text-gray-500 m-0">
                    Además: {d.canceladas > 0 && <>{d.canceladas} cancelada{d.canceladas !== 1 ? 's' : ''} o con revocación registrada</>}
                    {d.canceladas > 0 && d.renovadas > 0 && ' · '}
                    {d.renovadas > 0 && <>{d.renovadas} renovada{d.renovadas !== 1 ? 's' : ''}</>}
                  </p>
                )}
              </Tarjeta>

              <Tarjeta className="p-6">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">Configuración actual</p>
                {config ? (
                  <div className="space-y-2.5">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-sm text-gray-600">Valor</span>
                      <span className="text-xl font-extrabold text-gray-900 tabular-nums">{config.valorMatricula > 0 ? clp(config.valorMatricula) : 'Gratis'}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-gray-600">Vigencia</span>
                      <ChipNeutro>{config.tipoVigencia === 'anual' ? 'Anual (1 año)' : 'Indefinida'}</ChipNeutro>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-gray-600">Venta</span>
                      <ChipEstado activo={config.activa} labelActivo="En venta" labelInactivo="No se vende" />
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 m-0">Este recinto no tiene matrícula configurada.</p>
                )}
              </Tarjeta>
            </div>
          </div>
        </Spin>
      )}
    </div>
  );
};

export default EstadisticasMatriculas;
